import { useMemo } from 'react';

import { GenericResource } from '@/react/kubernetes/helm/types';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { StatusBadgeType } from '@@/StatusBadge';

import { ResourceLink, ResourceRow } from './types';

// from defined routes in app/kubernetes/__module.js
const kindToUrlMap = {
  Deployment: 'kubernetes.applications.application',
  DaemonSet: 'kubernetes.applications.application',
  StatefulSet: 'kubernetes.applications.application',
  Pod: 'kubernetes.applications.application',
  Ingress: 'kubernetes.ingresses',
  ConfigMap: 'kubernetes.configmaps.configmap',
  Secret: 'kubernetes.secrets.secret',
  PersistentVolumeClaim: 'kubernetes.volumes.volume',
  CustomResourceDefinition: 'kubernetes.moreResources.customResourceDefinition',
  CustomResource: 'kubernetes.moreResources.customResource',
};

const statusToColorMap: Record<string, StatusBadgeType> = {
  Healthy: 'success',
  Progressing: 'warning',
  Degraded: 'danger',
  Failed: 'danger',
  Unhealthy: 'danger',
  Unknown: 'info',
};

export function useResourceRows(
  resources?: GenericResource[],
  /** environmentId is required for callers rendered outside a kubernetes route */
  environmentId?: EnvironmentId
): ResourceRow[] {
  return useMemo(
    () => getResourceRows(resources, environmentId),
    [resources, environmentId]
  );
}

function getResourceRows(
  resources?: GenericResource[],
  environmentId?: EnvironmentId
): ResourceRow[] {
  if (!resources) {
    return [];
  }

  return resources.map((resource) => getResourceRow(resource, environmentId));
}

function getResourceRow(
  resource: GenericResource,
  environmentId?: EnvironmentId
): ResourceRow {
  const {
    reason = '',
    status = '',
    message = '',
  } = resource.status.healthSummary || {};

  return {
    id: `${resource.kind}/${resource.metadata.name}/${resource.metadata.namespace}`,
    name: {
      label: resource.metadata.name,
      link: getResourceLink(resource, environmentId),
    },
    resourceType: resource.kind ?? '-',
    describe: {
      name: resource.metadata.name,
      namespace: resource.metadata.namespace,
      resourceType: resource.kind,
      environmentId,
    },
    status: {
      label: reason ?? 'Unknown',
      type: statusToColorMap[status] ?? 'default',
    },
    statusMessage: message ?? '-',
  };
}

function getResourceLink(
  resource: GenericResource,
  environmentId?: EnvironmentId
): ResourceLink | null {
  const { namespace, name } = resource.metadata;

  const to = kindToUrlMap[resource.kind as keyof typeof kindToUrlMap];

  // If the resource kind is not supported, return null
  if (!to) {
    return null;
  }

  // endpointId is inherited from the current route when omitted; it is only set
  // explicitly for callers rendered outside a kubernetes route (e.g. add-ons).
  const endpointParam: Record<string, string> =
    environmentId !== undefined ? { endpointId: String(environmentId) } : {};

  // If the resource is not namespaced, return the link to the resource with the name only
  if (!namespace) {
    return {
      to,
      params: {
        ...endpointParam,
        name,
      },
    };
  }

  // If the resource is namespaced, return the link to the resource with the namespace and name
  return {
    to,
    params: {
      ...endpointParam,
      namespace,
      name,
    },
  };
}
